// Posición de un panel flotante anclado a un control.
//
// Existe porque un desplegable colocado con `position: absolute` dentro del
// propio control SÍ participa en el layout de su contenedor: si el control está
// pegado al borde derecho, el panel ensancha la página y aparece scroll
// horizontal. Y dentro de una tarjeta con `overflow-hidden` directamente se
// recorta.
//
// La solución es una sola para todos los desplegables: `position: fixed` en un
// portal, con la posición calculada contra el viewport. Así el panel nunca
// participa del layout ni puede recortarse.
//
// Garantiza `left >= MARGEN` y `right <= viewport - MARGEN`. Si abajo no cabe y
// arriba sí, se abre hacia arriba.
//
// El borde derecho se ancla con `right` y no con `left` a propósito. El ancho
// final del panel no se conoce aquí —lo decide su contenido entre `minWidth` y
// `maxWidth`—, así que calcular un `left` obligaría a suponerlo: suponer de
// menos deja el panel fuera de la ventana y suponer de más lo separa del campo
// sin motivo. Anclando por la derecha, el panel crece hacia la izquierda y su
// borde derecho queda donde se ha dicho, mida lo que mida.
import { useCallback, useLayoutEffect, useState, type RefObject } from 'react'

/** Aire mínimo contra los bordes de la ventana. */
const MARGEN = 8

export interface PosicionOverlay {
  top: number
  /** Se usa uno de los dos, nunca los dos: `left` en el caso normal y `right`
   *  cuando alinear por la izquierda sacaría el panel de la ventana. */
  left?: number
  right?: number
  /** Ancho del ancla: el panel nunca es más estrecho que su control. */
  minWidth: number
  /** Tope de ancho, ya recortado a lo que queda de ventana. */
  maxWidth: number
  /** Alto disponible: el panel scrollea por dentro si no cabe. */
  maxHeight: number
}

export function usePosicionOverlay(
  ancla: RefObject<HTMLElement | null>,
  abierto: boolean,
  { anchoMaximo = 400, altoDeseado = 300 }: { anchoMaximo?: number; altoDeseado?: number } = {},
): PosicionOverlay | null {
  const [pos, setPos] = useState<PosicionOverlay | null>(null)

  const medir = useCallback(() => {
    const nodo = ancla.current
    if (!nodo) return
    const r = nodo.getBoundingClientRect()

    const debajo = window.innerHeight - r.bottom - MARGEN
    const encima = r.top - MARGEN
    // Hacia arriba sólo si abajo no cabe y arriba hay más sitio.
    const haciaArriba = debajo < altoDeseado && encima > debajo
    const maxHeight = Math.max(120, Math.min(altoDeseado, haciaArriba ? encima : debajo))

    // Lo más ancho que el panel puede llegar a ser: su contenido lo estira hasta
    // el tope, y nunca puede pasar de lo que queda de ventana.
    const maxWidth = Math.min(
      Math.max(r.width, anchoMaximo),
      window.innerWidth - MARGEN * 2,
    )

    // Alineado a la izquierda del campo mientras quepa. Si en el peor caso se
    // saldría, se ancla por la derecha —al borde derecho del campo, o al de la
    // ventana si ni así cabe— y crece hacia dentro.
    const cabeALaIzquierda = r.left + maxWidth <= window.innerWidth - MARGEN
    const lado = cabeALaIzquierda
      ? { left: Math.max(MARGEN, r.left) }
      : { right: Math.max(MARGEN, window.innerWidth - r.right) }

    setPos({
      top: haciaArriba ? r.top - maxHeight - 4 : r.bottom + 4,
      ...lado,
      minWidth: Math.min(r.width, maxWidth),
      maxWidth,
      maxHeight,
    })
  }, [ancla, anchoMaximo, altoDeseado])

  useLayoutEffect(() => {
    if (!abierto) { setPos(null); return }
    medir()

    // `capture` para enterarse también del scroll de contenedores internos —una
    // tabla con scroll propio, el <main> de la app—, que no burbujea.
    window.addEventListener('scroll', medir, true)
    window.addEventListener('resize', medir)
    return () => {
      window.removeEventListener('scroll', medir, true)
      window.removeEventListener('resize', medir)
    }
  }, [abierto, medir])

  return pos
}
